import React from 'react';
import { History } from 'history';
import moment from 'moment';
import { Trans } from '@lingui/macro';
import { useQuery } from '@apollo/client';
import { Modal } from 'antd';

import { Query, CandidateShare, PagingData } from 'types';
import { RETURN_CANDIDATESHARE_INVITES_FOR_CANDIDATE } from 'api/candidate_shares_api';
import { defaultPagingData } from 'components/constants';
import { CANDIDATE_DASHBOARD_LINKS } from '../constants';
import { Button, Table } from 'components/AntD';

const columns = [
  { dataIndex: 'inviteeCompany', title: <Trans>Company</Trans> },
  { dataIndex: 'inviteeEmail', title: <Trans>Email</Trans> },
  {
    dataIndex: ['candidateShare', 'referencerDetails'],
    title: <Trans>Shared References</Trans>,
    render: (value: CandidateShare['referencerDetails']) =>
      (value || [])
        .map(candidateReferencerRelation => {
          const { referencerFirstName, referencerLastName } = candidateReferencerRelation;

          return `${referencerFirstName} ${referencerLastName}`;
        })
        .join(', '),
  },
  {
    dataIndex: 'invitedDate',
    title: <Trans>Invited Date</Trans>,
    render: (value: Date) => moment(value).format('MM/DD/YY'),
  },
  {
    dataIndex: 'verifiedDate',
    title: <Trans>Status</Trans>,
    render: (value: Date) => {
      if (value) {
        return (
          <div className="status-item invited-status">
            <Trans>Verified &amp; Sent</Trans>
          </div>
        );
      }
      return (
        <div className="status-item invited-status">
          <Trans>Pending Verification</Trans>
        </div>
      );
    },
  },
];

function CandidateSharePage({
  numSubmittedReferences,
  history,
}: CandidateSharePageProps): JSX.Element {
  const { data, loading } = useQuery<Pick<Query, 'fetchCandidateShareInvitesForMe'>>(
    RETURN_CANDIDATESHARE_INVITES_FOR_CANDIDATE
  );
  const [pagingData, setPagingData] = React.useState<PagingData>({ ...defaultPagingData });
  const [showDialog, setShowDialog] = React.useState(false);

  return (
    <div className="candidate-share-page">
      <Modal
        closeIcon={<i className="fas fa-times" />}
        visible={showDialog}
        onOk={() => {
          setShowDialog(false);
        }}
        title={<Trans>More references required</Trans>}
      >
        <Trans>You are unable to share until you have a minimum of two submitted references.</Trans>
      </Modal>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-justify-between tw-w-full sm:items-center tw-flex-col sm:tw-flex-row">
          <h5>
            <Trans>Share your full Searchlight report with a recruiter or hiring manager</Trans>
          </h5>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // either go to create flow or show an alert dialog
                if (numSubmittedReferences < 2) {
                  return setShowDialog(true);
                }
                history.push(CANDIDATE_DASHBOARD_LINKS.shares.newInvite);
              }}
            >
              <Trans>+ New Share</Trans>
            </Button>
          </div>
        </div>
        <p>
          <Trans>
            If you&apos;re actively interviewing, you can make a request to share your full
            reference report. We will automatically email the recipient to verify your request.
            <br />
            You do <em>not</em> need to share your references with companies that have directly
            invited you to Searchlight.
          </Trans>
        </p>

        {(data?.fetchCandidateShareInvitesForMe || []).length > 0 && (
          <>
            <hr className="tw-w-full" />
            <h5>
              <Trans>Your share history</Trans>
            </h5>

            <Table
              rowKey="candidateShareInviteId"
              data={{
                data: data?.fetchCandidateShareInvitesForMe || [],
                totalCount: data?.fetchCandidateShareInvitesForMe?.length || 0,
              }}
              columns={columns}
              locale={{
                emptyText: <Trans>There&rsquo;s nothing here yet!</Trans>,
              }}
              setPagingData={setPagingData}
              pagingData={pagingData}
              loading={loading}
              disableSearch
            />
          </>
        )}
      </div>
    </div>
  );
}

export interface CandidateSharePageProps {
  history: History;
  numSubmittedReferences: number;
}

export default CandidateSharePage;
